/* eslint indent: 0 */ // --> OFF
// 3rd Party
import { darken, lighten } from 'polished';
import styled from 'styled-components';

// Components
import { TYPOGRAPHY } from 'brand-guidelines/typography';
import { Button, Form, Icon, Input, Select, Skeleton } from 'opencare-crown';

// Other
import {
  BREAKPOINTS,
  colors,
  CONTENT_BREAKPOINTS,
  DARKEN_AMOUNT,
  FONT_FAMILIES,
} from './style-constants';

// General building blocks

interface DivProps {
  position?: string;
  top?: string;
  right?: string;
  bottom?: string;
  left?: string;
  width?: string;
  height?: string;
  maxWidth?: string;
  margin?: string;
  padding?: string;
  display?: string;
  background?: string;
  border?: string;
  borderbottom?: string;
  borderradius?: string;
  color?: string;
  cursor?: string;
  overflow?: string;
  textAlign?: string;
  opacity?: string;
  zIndex?: string;
}

/**
 * @deprecated Use a custom styled component, or <div> directly instead
 */
export const Div = styled.div.withConfig({
  shouldForwardProp: (prop) => ![
    'position',
    'top',
    'right',
    'bottom',
    'left',
    'width',
    'height',
    'maxWidth',
    'margin',
    'padding',
    'display',
    'background',
    'border',
    'borderbottom',
    'borderradius',
    'color',
    'cursor',
    'overflow',
    'textAlign',
    'opacity',
    'zIndex',
  ].includes(prop),
})<DivProps>`
  /* Positioning */
  position: ${(props) => props.position};
  top: ${(props) => props.top};
  right: ${(props) => props.right};
  bottom: ${(props) => props.bottom};
  left: ${(props) => props.left};

  /* Size */
  width: ${(props) => props.width};
  height: ${(props) => props.height};
  max-width: ${(props) => props.maxWidth};

  /* Spacing */
  margin: ${(props) => props.margin};
  padding: ${(props) => props.padding};

  /* Visual */
  display: ${(props) => props.display};
  background: ${(props) => props.background};
  border: ${(props) => props.border};
  border-bottom: ${(props) => props.borderbottom};
  border-radius: ${(props) => props.borderradius};

  /* Misc */
  color: ${(props) => props.color};
  cursor: ${(props) => props.cursor};
  overflow: ${(props) => props.overflow};
  text-align: ${(props) => props.textAlign};
  opacity: ${(props) => props.opacity};
  z-index: ${(props) => props.zIndex};
`;

interface SpanProps {
  display?: string;
  verticalAlign?: string;
  fontSize?: string;
  fontWeight?: string;
  textAlign?: string;
  lineHeight?: string;
  color?: string;
  textDecoration?: string;
  fontFamily?: string;
  margin?: string;
  padding?: string;
  borderbottom?: string;
}

/**
 * @deprecated Use a custom styled component, or <span> directly instead
 */
export const Span = styled.span.withConfig({
  shouldForwardProp: (prop) => !['display', 'verticalAlign', 'fontSize', 'fontWeight', 'textAlign', 'lineHeight', 'color', 'textDecoration', 'fontFamily', 'margin', 'padding', 'borderbottom'].includes(prop),
})<SpanProps>`
  /* Alignment */
  display: ${(props) => props.display};
  vertical-align: ${(props) => props.verticalAlign};

  /* Typography */
  font-size: ${(props) => props.fontSize};
  font-weight: ${(props) => props.fontWeight};
  text-align: ${(props) => props.textAlign};
  line-height: ${(props) => props.lineHeight};
  color: ${(props) => props.color};
  text-decoration: ${(props) => props.textDecoration};
  font-family: ${(props) => props.fontFamily};

  /* Spacing */
  margin: ${(props) => props.margin};
  padding: ${(props) => props.padding};

  /* Visual */
  border-bottom: ${(props) => props.borderbottom};
`;

/**
 * @deprecated Use a custom styled component, or <div> directly instead
 */
export const InlineDiv = styled(Div)`
  display: inline;
`;

interface FlexColProps {
  flexWrap?: string;
  justify?: string;
  align?: string;
  height?: string;
  gap?: string;
  mobileDirection?: string;
  mobileAlign?: string;
  mobileJustify?: string;
  mobileGap?: string;
}

/**
 * @deprecated Use a custom styled component, or <div> directly instead
 */
export const FlexCol = styled(Div).withConfig({
  shouldForwardProp: (prop) => !['flexWrap', 'justify', 'align', 'height', 'gap', 'mobileDirection', 'mobileAlign', 'mobileJustify', 'mobileGap'].includes(prop),
})<FlexColProps>`
  display: flex;
  flex-direction: column;
  flex-wrap: ${(props) => props.flexWrap};
  justify-content: ${(props) => props.justify};
  align-items: ${(props) => props.align};
  height: ${(props) => props.height};
  gap: ${(props) => props.gap};

  @media (max-width: ${BREAKPOINTS.mobileMax}px) {
    flex-direction: ${(props) => props.mobileDirection || 'column'};
    align-items: ${(props) => props.mobileAlign || props.align};
    justify-content: ${(props) => props.mobileJustify || props.justify};
    gap: ${(props) => props.mobileGap || props.gap};
  }
`;

interface FlexRowProps {
  flexWrap?: string;
  justify?: string;
  align?: string;
  gap?: string;
  flexDirection?: string;
  mobileDirection?: string;
  mobileAlign?: string;
  mobileJustify?: string;
  mobileGap?: string;
}

/**
 * @deprecated Use a custom styled component, or <div> directly instead
 */
export const FlexRow = styled(Div).withConfig({
  shouldForwardProp: (prop) => !['flexWrap', 'justify', 'align', 'gap', 'flexDirection', 'mobileDirection', 'mobileAlign', 'mobileJustify', 'mobileGap'].includes(prop),
})<FlexRowProps>`
  display: flex;
  flex-wrap: ${(props) => props.flexWrap};
  justify-content: ${(props) => props.justify};
  align-items: ${(props) => props.align};
  gap: ${(props) => props.gap};

  @media (max-width: ${BREAKPOINTS.mobileMax}px) {
    flex-direction: ${(props) =>
      props.mobileDirection || props.flexDirection || 'row'};
    align-items: ${(props) => props.mobileAlign || props.align};
    justify-content: ${(props) => props.mobileJustify || props.justify};
    gap: ${(props) => props.mobileGap || props.gap};
  }
`;

/**
 * @deprecated Use a custom styled component, or <div> directly instead
 */
export const FlexGrow = styled(Div)`
  flex: 1 1 auto;
`;

/**
 * @deprecated Use a custom styled component, or <div> directly instead
 */
export const FlexStatic = styled(Div)`
  flex: 0 0 auto;
`;

interface HRProps {
  border?: string;
  margin?: string;
  width?: string;
}

/**
 * @deprecated Use a custom styled component, or <hr> directly instead
 */
export const HR = styled.hr.withConfig({
  shouldForwardProp: (prop) => !['border', 'margin', 'width'].includes(prop),
})<HRProps>`
  border: ${(props) => props.border};
  margin: ${(props) => props.margin};
  width: ${(props) => props.width};
`;

interface H1Props {
  color?: string;
  fontSize?: string;
  fontWeight?: string;
  lineHeight?: string;
  textAlign?: string;
  display?: string;
  margin?: string;
  padding?: string;
  verticalAlign?: string;
}

/**
 * @deprecated Use a custom styled component, or <h1> directly instead
 */
export const H1 = styled.h1.withConfig({
  shouldForwardProp: (prop) => !['fontSize', 'fontWeight', 'lineHeight', 'textAlign', 'color', 'display', 'margin', 'padding', 'verticalAlign'].includes(prop),
})<H1Props>`
  /* Typography */
  font-size: ${(props) => props.fontSize || '22px'};
  font-weight: ${(props) => props.fontWeight};
  line-height: ${(props) => props.lineHeight};
  text-align: ${(props) => props.textAlign};
  color: ${(props) => props.color};

  /* Spacing */
  display: ${(props) => props.display};
  margin: ${(props) => props.margin};
  padding: ${(props) => props.padding};
  vertical-align: ${(props) => props.verticalAlign};
`;

interface H2Props {
  fontSize?: string;
  fontWeight?: string;
  lineHeight?: string;
  textAlign?: string;
  color?: string;
  display?: string;
  margin?: string;
  padding?: string;
  verticalAlign?: string;
}

/**
 * @deprecated Use a custom styled component, or <h2> directly instead
 */
export const H2 = styled.h2.withConfig({
  shouldForwardProp: (prop) => !['fontSize', 'fontWeight', 'lineHeight', 'textAlign', 'color', 'display', 'margin', 'padding', 'verticalAlign'].includes(prop),
})<H2Props>`
  /* Typography */
  font-size: ${(props) => props.fontSize || '18px'};
  font-weight: ${(props) => props.fontWeight || '600'};
  line-height: ${(props) => props.lineHeight};
  text-align: ${(props) => props.textAlign};
  color: ${(props) => props.color};

  /* Spacing */
  display: ${(props) => props.display};
  margin: ${(props) => props.margin};
  padding: ${(props) => props.padding};
  vertical-align: ${(props) => props.verticalAlign};
`;

interface H3Props {
  fontSize?: string;
  fontWeight?: string;
  lineHeight?: string;
  textAlign?: string;
  color?: string;
  display?: string;
  margin?: string;
  padding?: string;
  verticalAlign?: string;
}

/**
 * @deprecated Use a custom styled component, or <h3> directly instead
 */
export const H3 = styled.h3.withConfig({
  shouldForwardProp: (prop) => !['fontSize', 'fontWeight', 'lineHeight', 'textAlign', 'color', 'display', 'margin', 'padding', 'verticalAlign'].includes(prop),
})<H3Props>`
  /* Typography */
  font-size: ${(props) => props.fontSize || '16px'};
  font-weight: ${(props) => props.fontWeight || '600'};
  line-height: ${(props) => props.lineHeight};
  text-align: ${(props) => props.textAlign};
  color: ${(props) => props.color};

  /* Spacing */
  display: ${(props) => props.display};
  margin: ${(props) => props.margin};
  padding: ${(props) => props.padding};
  vertical-align: ${(props) => props.verticalAlign};
`;

interface H4Props {
  fontSize?: string;
  fontWeight?: string;
  lineHeight?: string;
  textAlign?: string;
  color?: string;
  display?: string;
  margin?: string;
  padding?: string;
  verticalAlign?: string;
}

/**
 * @deprecated Use a custom styled component, or <h4> directly instead
 */
export const H4 = styled.h4.withConfig({
  shouldForwardProp: (prop) => !['fontSize', 'fontWeight', 'lineHeight', 'textAlign', 'color', 'display', 'margin', 'padding', 'verticalAlign'].includes(prop),
})<H4Props>`
  /* Typography */
  font-size: ${(props) => props.fontSize || '14px'};
  font-weight: ${(props) => props.fontWeight || '600'};
  line-height: ${(props) => props.lineHeight};
  text-align: ${(props) => props.textAlign};
  color: ${(props) => props.color};

  /* Spacing */
  display: ${(props) => props.display};
  margin: ${(props) => props.margin};
  padding: ${(props) => props.padding};
  vertical-align: ${(props) => props.verticalAlign};
`;

interface H5Props {
  fontSize?: string;
  fontWeight?: string;
  lineHeight?: string;
  textAlign?: string;
  color?: string;
  display?: string;
  margin?: string;
  padding?: string;
  verticalAlign?: string;
}

/**
 * @deprecated Use a custom styled component, or <h5> directly instead
 */
export const H5 = styled.h5.withConfig({
  shouldForwardProp: (prop) => !['fontSize', 'fontWeight', 'lineHeight', 'textAlign', 'color', 'display', 'margin', 'padding', 'verticalAlign'].includes(prop),
})<H5Props>`
  /* Typography */
  font-size: ${(props) => props.fontSize || '12px'};
  font-weight: ${(props) => props.fontWeight || '600'};
  line-height: ${(props) => props.lineHeight};
  text-align: ${(props) => props.textAlign};
  color: ${(props) => props.color};

  /* Spacing */
  display: ${(props) => props.display};
  margin: ${(props) => props.margin};
  padding: ${(props) => props.padding};
  vertical-align: ${(props) => props.verticalAlign};
`;

interface PProps {
  fontSize?: string;
  fontWeight?: string;
  fontFamily?: string;
  lineHeight?: string;
  letterSpacing?: string;
  textAlign?: string;
  color?: string;
  textDecoration?: string;
  display?: string;
  margin?: string;
  padding?: string;
  verticalAlign?: string;
}

/**
 * @deprecated Use a custom styled component, or <p> directly instead
 */
export const P = styled.p.withConfig({
  shouldForwardProp: (prop) => !['fontSize', 'fontWeight', 'fontFamily', 'lineHeight', 'letterSpacing', 'textAlign', 'color', 'textDecoration', 'display', 'margin', 'padding', 'verticalAlign'].includes(prop),
})<PProps>`
  /* Typography */
  font-size: ${(props) => props.fontSize};
  font-weight: ${(props) => props.fontWeight};
  font-family: ${(props) => props.fontFamily || FONT_FAMILIES.openSans};
  line-height: ${(props) => props.lineHeight};
  letter-spacing: ${(props) => props.letterSpacing};
  text-align: ${(props) => props.textAlign};
  color: ${(props) => props.color};
  text-decoration: ${(props) => props.textDecoration};

  /* Spacing */
  display: ${(props) => props.display};
  margin: ${(props) => props.margin};
  padding: ${(props) => props.padding};
  vertical-align: ${(props) => props.verticalAlign};
`;

// Buttons
export const OCButton = styled(Button)`
  && {
    /* Typography */
    font-size: ${(props) => props.fontSize};
    font-weight: ${(props) => props.fontWeight || 600};
    font-family: ${FONT_FAMILIES.circular};
    color: ${(props) => props.color};

    /* Spacing */
    margin: ${(props) => props.margin};
    padding: ${(props) => props.padding};
    width: ${(props) => props.width};
    height: ${(props) => props.height || 'auto'};
    align-self: ${(props) => props.alignself};

    /* Visual */
    background: ${(props) => props.background};
    border: ${(props) => props.border || 'none'};
    border-radius: ${(props) => props.borderradius || '25px'};
  }

  &:focus,
  &:hover {
    && {
      background: ${(props) =>
        (props.background
          ? darken(DARKEN_AMOUNT, props.background)
          : 'none')} !important;
      border: ${(props) => props.border || 'none'} !important;
      color: ${(props) => props.color} !important;
    }
  }

  &.ant-btn-link:hover {
    span {
      text-decoration: underline;
    }
  }

  i,
  .anticon {
    vertical-align: 0.09em;
  }

  &[disabled] {
    &:hover {
      && {
        background: #f5f5f5;
        color: rgba(0, 0, 0, 0.25);
      }
    }
  }
`;

/**
 * @deprecated Use a custom styled component, or <Button> directly instead
 */
export const MarketingButton = styled(Button)`
  && {
    /* This is what makes it marketing vs product */
    font-family: ${FONT_FAMILIES.circular};
    font-weight: 600;

    /* Ability to style */
    margin: ${(props) => props.margin || 'initial'};
    max-width: ${(props) => props.maxwidth || 'initial'};

    &.ant-btn-lg {
      padding: 15px 20px;
      height: auto;
    }
  }
`;

/**
 * @deprecated Use a custom styled component, or <Button> directly instead
 * To be used on color backgrounds. Ideally we should use Button from opencare-crown
 * and set ghost property to true rather than use this component.
 */
export const FilledGhostButton = styled(Button)`
  && {
    font-family: ${FONT_FAMILIES.circular};
    font-weight: 600;
    line-height: 1.5715;
    background: ${colors.white};
    color: ${(props) => props.color || colors.charcoal};
    border-color: ${(props) => props.color || colors.white};

    &:hover,
    &:focus {
      color: ${(props) => props.color || colors.charcoal} !important;
      border-color: ${(props) => props.color || colors.white} !important;
      opacity: 0.92;
    }
  }
`;

/**
 * @deprecated Use a custom styled component, or <Button> directly instead
 */
export const ColoredButton = styled(Button)`
  && {
    background: ${(props) => props.background || colors.sage5};
    color: ${(props) => props.color || colors.white};
    border-color: ${(props) => props.color};
    font-weight: ${(props) => (props.bold === 'true' ? '900' : '600')};
    ${(props) =>
      (props.bold === 'true'
        ? TYPOGRAPHY.CIRCULAR.bold
        : TYPOGRAPHY.CIRCULAR.book)};
    height: auto !important;
    padding-top: ${(props) =>
      (props.size === 'large' ? '12px' : '7px')} !important;
    padding-bottom: ${(props) =>
      (props.size === 'large' ? '12px' : '7px')} !important;
    padding-left: ${(props) =>
      (props.size === 'large' ? '30px' : '16px')} !important;
    padding-right: ${(props) =>
      (props.size === 'large' ? '30px' : '16px')} !important;
    max-width: ${(props) => props.maxwidth || 'initial'};

    &:disabled {
      color: ${(props) => props.color || colors.white} !important;
      background: ${(props) =>
        lighten(0.3, props.background || colors.sage5)} !important;
      border-color: ${(props) =>
        lighten(0.3, props.background || colors.sage5)} !important;
    }

    &:not(:disabled) {
      &:hover,
      &:focus {
        color: ${(props) => props.color || colors.white} !important;
        background: ${(props) =>
          lighten(0.1, props.background || colors.sage5)} !important;
        border-color: ${(props) =>
          lighten(0.1, props.background || colors.sage5)} !important;
      }
    }

    /* For some reason in search-v2 the loading icon in buttons isn't vertically aligned */
    > .ant-btn-loading-icon .anticon {
      vertical-align: 0.1em;
    }
  }
`;

interface BlueLinkProps {
  underline?: boolean;
}

/**
 * @deprecated Use a custom styled component, or <a> directly instead
 */
export const BlueLink = styled.a.withConfig({
  shouldForwardProp: (prop) => !['underline'].includes(prop),
})<BlueLinkProps>`
  color: ${colors.blue};
  text-decoration: ${(props) => (props.underline ? 'underline' : 'inherit')};

  &:focus,
  &:hover {
    color: ${darken(DARKEN_AMOUNT, colors.blue)};
  }
`;

/**
 * @deprecated Use a custom styled component, or <div> directly instead
 */
export const AbsoluteContainer = styled(Div)`
  position: absolute;
`;

interface ContainerProps {
  flexDirection?: string;
  height?: string;
  minHeight?: string;
  margin?: string;
  padding?: string;
}

/**
 * @deprecated Use a custom styled component, or <div> directly instead
 */
export const Container = styled(FlexRow).withConfig({
  shouldForwardProp: (prop) => !['flexDirection', 'height', 'minHeight', 'margin', 'padding'].includes(prop),
})<ContainerProps>`
  flex-direction: ${(props) => props.flexDirection || 'row'};
  height: ${(props) => props.height};
  min-height: ${(props) => props.minHeight};
  margin: ${(props) => props.margin || '0 auto'};
  padding: ${(props) => props.padding};

  /* The order of the media selectors matter */
  @media (max-width: ${BREAKPOINTS.tabletMax}px) {
    width: ${CONTENT_BREAKPOINTS.tablet}px;
  }

  @media (max-width: ${BREAKPOINTS.mobileMax}px) {
    width: 100%;
  }

  @media (min-width: ${BREAKPOINTS.desktopMin}px) {
    width: ${CONTENT_BREAKPOINTS.desktop}px;
  }

  @media (min-width: ${BREAKPOINTS.widescreenMin}px) {
    width: ${CONTENT_BREAKPOINTS.widescreen}px;
  }
`;

interface ZIndexContainerProps {
  align?: string;
  display?: string;
  flexDirection?: string;
  justify?: string;
  zIndex?: string;
}

/**
 * @deprecated Use a custom styled component, or <div> directly instead
 */
export const ZIndexContainer = styled(Div).withConfig({
  shouldForwardProp: (prop) => !['align', 'display', 'flexDirection', 'justify', 'zIndex'].includes(prop),
})<ZIndexContainerProps>`
  align-items: ${(props) => props.align};
  display: ${(props) => props.display};
  flex-direction: ${(props) => props.flexDirection};
  justify-content: ${(props) => props.justify};
  z-index: ${(props) => props.zIndex};
`;

interface ResultsHeaderContainerProps {
  background?: string;
}

/**
 * @deprecated Use a custom styled component, or <div> directly instead
 */
export const ResultsHeaderContainer = styled(FlexCol).withConfig({
  shouldForwardProp: (prop) => !['background'].includes(prop),
})<ResultsHeaderContainerProps>`
  background: ${(props) => props.background};
  box-shadow: 2px 2px 10px 0 rgba(0, 0, 0, 0.05);
`;

interface SvgContainerProps {
  color?: string;
  margin?: string;
  height?: string;
  width?: string;
}

/**
 * @deprecated Use a custom styled component, or <div> directly instead
 */
export const SvgContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !['color', 'margin', 'height', 'width'].includes(prop),
})<SvgContainerProps>`
  display: inline;
  color: ${(props) => props.color};
  margin: ${(props) => props.margin};

  & > svg {
    color: ${(props) => props.color};
    height: ${(props) => props.height};
    width: ${(props) => props.width};
  }
`;

interface BasicContainerProps {
  height?: string;
  slim?: boolean;
  maxWidth?: string;
}

/**
 * @deprecated Use a custom styled component, or <div> directly instead
 */
export const BasicContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !['height', 'slim', 'maxWidth'].includes(prop),
})<BasicContainerProps>`
  width: 100%;
  height: ${(props) => props.height || 'initial'};
  padding: ${(props) => (props.slim ? '0 5px' : '0 15px')};
  max-width: ${(props) => props.maxWidth || '1170px'};
  margin: 0 auto;
  position: relative;
`;

interface OCBadgeProps {
  width?: string;
  margin?: string;
  color?: string;
}

/**
 * @deprecated Use a custom styled component, or <div> directly instead
 */
export const OCBadge = styled.div.withConfig({
  shouldForwardProp: (prop) => !['width', 'margin', 'color'].includes(prop),
})<OCBadgeProps>`
  /* Size */
  width: ${(props) => props.width};

  /* Spacing */
  margin: ${(props) => props.margin};
  padding: 6px 10px;

  /* Typography */
  font-size: 12px;
  font-family: ${FONT_FAMILIES.openSans};
  color: ${colors.white};
  text-align: center;

  /* Visual */
  background-color: ${(props) => props.color};
  border-radius: 3px;
  display: inline-block;

  span {
    margin-right: 5px;
    vertical-align: 0.07em;
  }
`;

interface ColorIconProps {
  color?: string;
  margin?: string;
}

/**
 * @deprecated Use a custom styled component, or <div> directly instead
 */
export const ColorIcon = styled(Icon)<ColorIconProps>`
  && {
    color: ${(props) => props.color};
    vertical-align: 0.08em;
    margin: ${(props) => props.margin || '0 5px 0 0'};
  }
`;

interface OCInputProps {
  width?: string;
  height?: string;
  margin?: string;
  padding?: string;
}

/**
 * @deprecated Use a custom styled component, or <input> directly instead
 */
export const OCInput = styled(Input).withConfig({
  shouldForwardProp: (prop) => !['width', 'height', 'margin', 'padding'].includes(prop),
})<OCInputProps>`
  /* Visual */
  background-color: ${colors.white};
  background-image: none;
  border: 1px solid ${colors.silver};
  border-radius: 3px;

  /* Typography */
  color: ${colors.charcoal};
  font-weight: 600;

  /* Alignment */
  display: block;
  padding: ${(props) => props.padding};
  width: ${(props) => props.width || '100%'};
`;

interface TextSkeletonProps {
  height?: string;
  margin?: string;
  width?: string;
}

/**
 * @deprecated Use a custom styled component, or <Skeleton> directly instead
 */
export const TextSkeleton = styled(Skeleton).withConfig({
  shouldForwardProp: (prop) => !['height', 'margin', 'width'].includes(prop),
})<TextSkeletonProps>`
  height: ${(props) => props.height};

  && {
    display: flex;
  }

  .ant-skeleton-content > .ant-skeleton-title {
    height: ${(props) => props.height};
    margin: ${(props) => props.margin};
    width: ${(props) => props.width};
  }
`;

interface VisuallyHiddenProps {
  height?: string;
  margin?: string;
  width?: string;
}

/**
 * @deprecated Use a custom styled component, or <span> directly instead
 * Based on https://reach.tech/visually-hidden
 */
export const VisuallyHidden = styled.span.withConfig({
  shouldForwardProp: (prop) => !['height', 'margin', 'width'].includes(prop),
})<VisuallyHiddenProps>`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
  white-space: nowrap;
  word-wrap: normal;
`;

/**
 * @deprecated Use a custom styled component, or <p> directly instead
 */
export const InputLabel = styled(P)`
  position: absolute;
  top: 9px;
  left: 12px;
  font-size: 12px;
  color: ${colors.gray8};
`;

interface StyledFormItemProps {
  marginbottom?: string;
  margintop?: string;
  paddingButtom?: string;
}

/**
 * @deprecated Use a custom styled component, or <Form.Item> directly instead
 */
export const StyledFormItem = styled(Form.Item).withConfig({
  shouldForwardProp: (prop) => !['marginbottom', 'margintop', 'paddingButtom'].includes(prop),
})<StyledFormItemProps>`
  && {
    margin-bottom: ${(props) => props.marginbottom || '0px'};
    margin-top: ${(props) => props.margintop};

    &.ant-form-item {
      padding-bottom: ${(props) => props.paddingButtom || '12px'};
    }

    .ant-form-explain {
      margin-top: 2px;
    }
  }
`;

interface StyledSelectProps {
  maxwidth?: string;
}

/**
 * @deprecated Use a custom styled component, or <Select> directly instead
 */
export const StyledSelect = styled(Select)
  .withConfig({ shouldForwardProp: (prop) => !['maxwidth'].includes(prop) })
  .attrs({ getPopupContainer: (triggerNode: HTMLElement) => triggerNode.parentNode })<StyledSelectProps>`
  && {
    max-width: ${(props) => props.maxwidth || null};

    &:focus-within {
      outline: none !important;
    }

    &.ant-select-single:not(.ant-select-customize-input) .ant-select-selector {
      height: 60px;
      width: 100%;
      font-size: 16px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding: 24px 2px 10px;
      overflow: hidden;

      border-radius: 10px;
      box-shadow: inset 0px 2px 3px rgba(0, 0, 0, 0.1);
      border-color: ${colors.gray6};

      &:active,
      &:focus {
        border-color: ${colors.blue};
        box-shadow: 0 0 3px ${colors.blue};
        outline: none !important;
      }

      &:hover {
        border-color: ${colors.blue};
        box-shadow: inset 0px 2px 3px rgba(0, 0, 0, 0.05);
      }
    }

    .ant-select-selection__rendered {
      margin-left: 8px;
    }

    .ant-select-arrow {
      color: ${colors.gray8};
      right: 8px;
    }

    &.ant-select-single:not(.ant-select-customize-input) .ant-select-selection-item {
      position: absolute;
      left: 0;
      padding-left: 11px;
      color: ${colors.gray9};
      font-weight: 600;
      overflow: hidden;
      width: 100%;
      bottom: 4px;
    }

    &.ant-select-single:not(.ant-select-customize-input).ant-select-open .ant-select-selection-item {
      opacity: 0.4;
    }

    .ant-select-selection-placeholder {
      position: absolute;
      color: ${colors.gray7};
      left: 9px;
      right: auto;
      overflow: initial;
    }

    .ant-select-selection-search-input {
      position: absolute;
      bottom: 4px;
    }
  }
`;

/**
 * @deprecated Use a custom styled component, or <span> directly instead
 * Avoid wrapping this span unless necessary
 * src: https://stackoverflow.com/a/10104013
 */
export const AvoidWrap = styled.span.withConfig({
  shouldForwardProp: (prop) => !['display'].includes(prop),
})`
  display: inline-block;
`;
